import { FormEvent, useEffect, useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { Button, Card, Faelligkeit, Sheet, formatDatum, toast } from '@elbklar/ui';

import { TextareaField } from '@/components/Felder';
import { radarApi } from '@/lib/api';
import { pflicht, zumErstenFehler } from '@/lib/pruefen';
import type { RadarProgram } from '@/types';
import { KIND_TEXT } from './radarText';
import { QuellLinks, Quellzustand, useVorwarnungTage } from './Bausteine';

interface Props {
  programme: RadarProgram[];
  onGeaendert: () => void;
  /** Suchparameter der Liste, damit „Zurück zum Radar“ auf der Programmseite dorthin führt. */
  listenSuche?: string;
}

/** Anker der Prüfaufträge: Mail-Link `/radar#pruefauftraege` (Auftrag 010, D2). */
export const PRUEFAUFTRAEGE_ANKER = 'pruefauftraege';

/**
 * Die Prüfaufträge — Quellen, die der Roboter nicht lesen kann.
 *
 * Hier steht alles, was Anton braucht, um in einer Minute nachzusehen:
 * der Link, der fertige Suchbegriff und zwei Knöpfe für das Ergebnis.
 */
export function PruefauftraegeBlock({ programme, onGeaendert, listenSuche = '' }: Props) {
  const [geaendertFuer, setGeaendertFuer] = useState<RadarProgram | null>(null);
  const vorwarnungTage = useVorwarnungTage();

  const unveraendert = useMutation({
    mutationFn: (id: string) => radarApi.checked(id, { changed: false }),
    onSuccess: () => {
      toast.success('Geprüft — unverändert');
      onGeaendert();
    },
    onError: () => toast.error('Vermerken fehlgeschlagen'),
  });

  if (programme.length === 0) return null;

  return (
    <>
      <section id={PRUEFAUFTRAEGE_ANKER} className="scroll-mt-4">
        <Card
          icon={Eye}
          title={
            <>
              Prüfaufträge <span className="ml-1 font-normal text-muted-foreground">{programme.length}</span>
            </>
          }
          description="Diese Quellen kann das Radar nicht selbst lesen. Kurz nachsehen, ob sich etwas geändert hat — dann ist der Auftrag erledigt."
        >
          <ul className="divide-y divide-border">
            {programme.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/radar/${p.id}`}
                    state={{ listenSuche }}
                    className="flex min-h-6 max-w-full items-center font-medium hover:text-link hover:underline touch:min-h-11"
                  >
                    <span className="truncate">{p.title}</span>
                  </Link>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 t-small text-muted-foreground">
                    <span>{KIND_TEXT[p.kind]}</span>
                    <Quellzustand zustand={p.source_state} />
                    {p.deadline &&
                      (p.deadline_kind === 'fest' ? (
                        <Faelligkeit datum={p.deadline} relativ vorwarnungTage={vorwarnungTage} vergangenRuhig />
                      ) : (
                        <span>Frist {formatDatum(p.deadline)}</span>
                      ))}
                    {p.last_manual_check_at && <span>zuletzt geprüft {formatDatum(p.last_manual_check_at)}</span>}
                    <QuellLinks programm={p} />
                  </div>
                </div>

                <PruefKnoepfe
                  laeuft={unveraendert.isPending && unveraendert.variables === p.id}
                  onUnveraendert={() => unveraendert.mutate(p.id)}
                  onGeaendert={() => setGeaendertFuer(p)}
                />
              </li>
            ))}
          </ul>
        </Card>
      </section>

      <GeaendertDialog
        programm={geaendertFuer}
        onSchliessen={() => setGeaendertFuer(null)}
        onGespeichert={onGeaendert}
      />
    </>
  );
}

/** Die zwei Knöpfe eines Prüfauftrags — in der Liste und auf der Programmseite gleich. */
export function PruefKnoepfe({
  laeuft,
  onUnveraendert,
  onGeaendert,
}: {
  laeuft: boolean;
  onUnveraendert: () => void;
  onGeaendert: () => void;
}) {
  return (
    // Volle Knopfgröße: Prüfaufträge erledigt man auch am Handy (44 px).
    // Am Handy dürfen die Knöpfe umbrechen (`shrink-0` erst ab sm): Zwei in
    // voller Größe passen nicht in 390 px und schoben die Seite seitlich.
    <div className="flex min-w-0 flex-wrap gap-2 sm:shrink-0">
      <Button loading={laeuft} onClick={onUnveraendert}>
        Geprüft — unverändert
      </Button>
      <Button onClick={onGeaendert}>
        Geprüft — geändert…
      </Button>
    </div>
  );
}

/**
 * Was hat sich geändert? Der Befund landet datiert in der Notiz — die
 * nächste Prüfung überschreibt ihn nicht.
 */
export function GeaendertDialog({
  programm,
  onSchliessen,
  onGespeichert,
}: {
  programm: RadarProgram | null;
  onSchliessen: () => void;
  onGespeichert: () => void;
}) {
  const formId = useId();
  const [notiz, setNotiz] = useState('');
  const [fehler, setFehler] = useState<string>();

  // Ein anderes Programm bekommt ein leeres Feld, nicht den Befund des vorigen.
  const programmId = programm?.id;
  useEffect(() => {
    setNotiz('');
    setFehler(undefined);
  }, [programmId]);

  const speichern = useMutation({
    mutationFn: () =>
      radarApi.checked(programm!.id, { changed: true, note: notiz.trim() || null }),
    onSuccess: () => {
      toast.success('Geprüft — Befund vermerkt');
      setNotiz('');
      onGespeichert();
      onSchliessen();
    },
    onError: () => toast.error('Vermerken fehlgeschlagen'),
  });

  // „Geprüft — geändert“ ohne Befund hielte nur den Zeitpunkt fest, nicht was
  // sich geändert hat; für „unverändert“ gibt es den eigenen Knopf.
  const absenden = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Das Sheet hängt im Portal, React reicht `submit` aber den Baum hinauf:
    // Auf der Programmseite käme es sonst auch im Formular der Seite an.
    event.stopPropagation();
    const neu = pflicht(notiz, 'Befund fehlt');
    setFehler(neu);
    if (neu) {
      zumErstenFehler(event.currentTarget);
      return;
    }
    speichern.mutate();
  };

  return (
    <Sheet
      open={programm !== null}
      onOpenChange={(o) => !o && onSchliessen()}
      title="Geprüft — was hat sich geändert?"
      description={programm?.title}
      footer={
        <>
          <Button variant="ghost" onClick={onSchliessen} disabled={speichern.isPending}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} variant="primary" loading={speichern.isPending}>
            Befund vermerken
          </Button>
        </>
      }
    >
      <form noValidate id={formId} onSubmit={absenden}>
        <TextareaField
          label="Befund"
          rows={4}
          placeholder="z. B. Frist auf 30.11. verschoben, Fördersatz jetzt 40 %"
          value={notiz}
          onChange={(e) => {
            setNotiz(e.target.value);
            setFehler(undefined);
          }}
          error={fehler}
          required
        />
      </form>
    </Sheet>
  );
}
